<!-- Fixed FAQScreen.vue with immediate direct navigation -->
<template>
  <div class="h-full">
    <!-- Categories Screen (first level) -->
    <FAQCategoriesScreen
      v-if="currentView === 'categories'"
      :isMobile="isMobile"
      :styles="styles"
      @view-category="handleViewCategory"
      @change-screen="$emit('change-screen', $event)"
      @close-widget="$emit('close-widget')"
    />
    
    <!-- FAQ List Screen (second level) -->
    <FAQListScreen
      v-else-if="currentView === 'list'"
      :selected-category="selectedCategory"
      :isMobile="isMobile"
      :styles="styles"
      @go-back="handleGoBackToCategories"
      @view-faq="handleViewFAQ"
      @change-screen="$emit('change-screen', $event)"
      @close-widget="$emit('close-widget')"
    />
    
    <!-- FAQ Detail Screen (third level) -->
    <FAQDetailScreen
      v-else-if="currentView === 'detail'"
      :faq="selectedFAQ"
      :isExpanded="isExpanded"
      :isMobile="isMobile"
      :styles="styles"
      :recommendedFAQs="recommendedFAQs"
      @go-back="handleGoBackToList"
      @expand-faq="handleExpandFAQ"
      @collapse-faq="handleCollapseFAQ"
      @view-faq="handleViewFAQ"
      @rate-faq="handleRateFAQ"
      @close-widget="$emit('close-widget')"
    />
    
    <!-- Loading state during direct navigation -->
    <div v-else-if="currentView === 'loading'" class="h-full flex items-center justify-center">
      <div class="text-center">
        <div class="w-8 h-8 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-4"></div>
        <p class="text-gray-600">FAQ wird geladen...</p>
      </div>
    </div>
    
    <!-- Fallback if something goes wrong -->
    <div v-else class="p-4 text-center">
      <p class="text-red-500">Error: Unknown view "{{ currentView }}"</p>
      <button @click="reset()" class="mt-2 px-4 py-2 bg-blue-500 text-white rounded">
        Reset to Categories
      </button>
    </div>
  </div>
</template>

<script>
import FAQListScreen from '../components/faq/FAQListScreen.vue';
import FAQCategoriesScreen from '../components/faq/FAQCategoriesScreen.vue';
import FAQDetailScreen from '../components/faq/FAQDetailScreen.vue';
import { fetchFAQsByCategory, fetchFAQById } from '../lib/appwriteFaq';

export default {
  name: 'FAQScreen',
  components: {
    FAQCategoriesScreen,
    FAQListScreen,
    FAQDetailScreen
  },
  props: {
    isExpanded: {
      type: Boolean,
      default: false
    },
    isMobile: {
      type: Boolean,
      default: false
    },
    styles: {
      type: Object,
      default: () => ({})
    },
    // 🆕 Direct FAQ navigation prop
    directFaq: {
      type: Object,
      default: null
    }
  },
  emits: ['change-screen', 'close-widget', 'expand-faq', 'collapse-faq'],
  data() {
    return {
      // ✅ FIXED: Start with loading if we have direct FAQ, otherwise categories
      currentView: 'categories',
      selectedCategory: null,
      selectedFAQ: null,
      recommendedFAQs: [],
      isProcessingDirectFaq: false
    };
  },
  // ✅ CRITICAL: Process direct FAQ immediately when component is created
  created() {
    
    if (this.directFaq && this.directFaq.directToFaq) {
      this.currentView = 'loading';
      this.handleDirectFaqNavigation(this.directFaq);
    }
  },
  watch: {
    // 🆕 Watch for direct FAQ changes (if prop changes after mount)
    directFaq: {
      handler(newDirectFaq) {
        
        if (newDirectFaq && newDirectFaq.directToFaq && !this.isProcessingDirectFaq) {
          this.currentView = 'loading';
          this.handleDirectFaqNavigation(newDirectFaq);
        }
      },
      immediate: false // Don't run immediately since we handle it in created()
    }
  },
  mounted() {

  },
  methods: {
    // ✅ ENHANCED: Handle direct FAQ navigation with proper FAQ fetching
    async handleDirectFaqNavigation(directFaqEvent) {
      
      if (this.isProcessingDirectFaq) {
        return;
      }
      
      this.isProcessingDirectFaq = true;
      
      try {
        // ✅ Set loading state
        this.currentView = 'loading';
        
        // ✅ Add a small delay to ensure UI updates
        await new Promise(resolve => setTimeout(resolve, 100));
        
        let faqData = null;
        
        // 🆕 ENHANCED: Fetch full FAQ data if we only have ID and title
        if (directFaqEvent.faqId && (!directFaqEvent.faqData || directFaqEvent.faqData.answer === 'Loading...')) {
          
          try {
            // Fetch the complete FAQ data from the database
            faqData = await fetchFAQById(directFaqEvent.faqId);
          } catch (error) {
            console.error('❌ Error fetching FAQ by ID:', error);
            // Fallback to partial data if fetch fails
            faqData = directFaqEvent.faqData || {
              $id: directFaqEvent.faqId,
              question: directFaqEvent.faqTitle || 'FAQ',
              answer: 'Sorry, this FAQ content could not be loaded.',
              category: directFaqEvent.category || 'General'
            };
          }
        } else {
          // Use provided FAQ data
          faqData = directFaqEvent.faqData;
        }
        
        // ✅ Set the FAQ data
        this.selectedFAQ = faqData;
        
        
        // ✅ Set category if available
        if (directFaqEvent.category || faqData.category) {
          this.selectedCategory = directFaqEvent.category || faqData.category;
        }
        
        // ✅ Load recommended FAQs
        await this.loadRecommendedFAQs(faqData);
        
        // ✅ Navigate to detail view
        this.currentView = 'detail';
        
        
 
        
      } catch (error) {
        console.error('❌ FAQ Screen: Error in direct navigation:', error);
        
        // Show error message to user
        alert('Fehler beim Laden des FAQ-Artikels. Bitte versuchen Sie es erneut.');
        
        // Fallback to categories
        this.currentView = 'categories';
        this.selectedFAQ = null;
        this.selectedCategory = null;
        this.recommendedFAQs = [];
        
      } finally {
        // Reset processing flag
        setTimeout(() => {
          this.isProcessingDirectFaq = false;
        }, 500);
      }
    },
    
    // Handle when a category is selected from the categories screen
    handleViewCategory(category) {
      this.selectedCategory = category;
      this.currentView = 'list';
    },
    
    // Handle when an FAQ is selected from the list screen
    async handleViewFAQ(faq) {
      
      if (!faq) {
        console.error('❌ FAQ Screen: No FAQ provided');
        return;
      }
      
      this.selectedFAQ = faq;
      this.currentView = 'detail';
      
      // Load recommended FAQs
      await this.loadRecommendedFAQs(faq);
      
    },
    
    // Handle going back to categories from list view
    handleGoBackToCategories() {
      this.currentView = 'categories';
      this.selectedCategory = null;
    },
    
    // Handle going back from detail view
    handleGoBackToList() {
      
      if (this.selectedCategory) {
        this.currentView = 'list';
        this.selectedFAQ = null;
      } else {
        this.currentView = 'categories';
        this.selectedFAQ = null;
      }
    },
    
    // Handle expand FAQ functionality
    handleExpandFAQ() {
      this.$emit('expand-faq');
    },
    
    // Handle collapse FAQ functionality
    handleCollapseFAQ() {
      this.$emit('collapse-faq');
    },
    
    // Handle rating submission
    handleRateFAQ(ratingData) {
    },
    
    // Load recommended FAQs based on current FAQ
    async loadRecommendedFAQs(currentFAQ) {
      try {
        
        if (currentFAQ.category) {
          const categoryFAQs = await fetchFAQsByCategory(currentFAQ.category);
          
          // Filter out the current FAQ and take up to 3 recommendations
          this.recommendedFAQs = categoryFAQs
            .filter(faq => faq.$id !== currentFAQ.$id)
            .slice(0, 3);
            
        } else {
          this.recommendedFAQs = [];
        }
        
      } catch (error) {
        console.error('❌ Error loading recommended FAQs:', error);
        this.recommendedFAQs = [];
      }
    },
    
    // Reset to the initial state
    reset() {      this.currentView = 'categories';
      this.selectedCategory = null;
      this.selectedFAQ = null;
      this.recommendedFAQs = [];
      this.isProcessingDirectFaq = false;
    }
  }
};
</script>